import { Link, useParams, Navigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { CTASection } from "@/components/CTASection";
import { CASES } from "@/lib/data";
import { useReveal } from "@/hooks/useReveal";

export default function CaseDetail() {
  const { slug } = useParams();
  const item = CASES.find((c) => c.slug === slug);
  const ref1 = useReveal();
  const ref2 = useReveal();

  if (!item) return <Navigate to="/cases" replace />;

  const related = CASES.filter((c) => c.slug !== slug).slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: item.headline,
    description: item.description,
    image: item.image,
    author: { "@type": "Organization", name: "LD Media" },
    publisher: {
      "@type": "Organization",
      name: "LD Media",
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_5e88ad15-a14f-4eb7-a874-cac507979086/artifacts/haj2wfbo_1000000444.jpg",
      },
    },
    about: item.name,
  };

  return (
    <>
      <SEO
        title={`${item.name} – Case`}
        path={`/cases/${item.slug}`}
        description={item.description}
        image={item.image}
        type="article"
        jsonLd={jsonLd}
      />

      <section className="relative pt-28 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="glow-blob bg-[hsl(var(--accent))] -top-32 -left-32 w-[420px] h-[420px]" style={{ opacity: 0.18 }} />
        <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-10">
          <div className="mb-6">
            <Link to="/cases" className="text-sm text-white/60 hover:text-[hsl(var(--accent))] link-underline">
              ← Alle cases
            </Link>
          </div>
          <div ref={ref1} className="reveal">
            <div className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))] font-semibold">
              {item.category}
            </div>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-[56px] font-bold mt-3 leading-[1.05]">
              {item.name}
            </h1>
            <p className="mt-5 text-xl sm:text-2xl text-white/75 font-light leading-snug max-w-3xl">
              {item.headline}
            </p>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="mx-auto max-w-5xl px-5 lg:px-10">
          <div ref={ref2} className="reveal rounded-3xl overflow-hidden border border-white/10">
            <img src={item.image} alt={`${item.name} case study`} className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-5 text-white/75 leading-relaxed">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--accent))] font-semibold">Casen</div>
            {item.longDescription.map((p, i) => (
              <p key={i} className="text-base sm:text-lg">
                {p}
              </p>
            ))}
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-3xl border border-white/10 bg-[hsl(var(--bg-card))] p-6">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Services leveret</div>
              <ul className="space-y-2.5">
                {item.services.map((s) => (
                  <li key={s} className="flex items-start gap-2.5 text-sm text-white/85">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            <Link
              to="/kontakt"
              className="btn-pop block text-center px-6 py-4 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-ink))] font-semibold hover:shadow-[0_12px_40px_-8px_hsla(75,100%,67%,0.7)]"
            >
              Få et lignende setup →
            </Link>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-12 lg:py-16 border-t border-white/10">
          <div className="mx-auto max-w-7xl px-5 lg:px-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-8">Flere cases</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/cases/${r.slug}`}
                  className="group block rounded-3xl overflow-hidden border border-white/10 bg-[hsl(var(--bg-card))] hover:border-[hsl(var(--accent))]/60 transition-colors"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={r.image}
                      alt={r.name}
                      className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute bottom-4 left-5 right-5">
                      <div className="text-xs text-[hsl(var(--accent))] uppercase tracking-widest font-semibold">
                        {r.category}
                      </div>
                      <div className="font-display text-xl font-bold mt-1">{r.name}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </>
  );
}
